import {
  DROP_INITIAL_SPEED,
  DROP_INTERVAL_MS,
  DROP_SETTLE_MS,
  FIELD_HEIGHT,
  FIELD_WIDTH,
  MERGE_DANGER_GRACE_MS,
} from '../../config/gameplay';
import { getCard, type CardId } from '../content/cards';
import { DEFAULT_GAME_MODE, getGameMode, getModePhase, type GameModeId } from '../content/modes';
import { getCharacter, getCharacterForPhase, type CharacterId, type CharacterSpecialKind } from '../content/characters';
import {
  CARD_HAND_LIMIT,
  awardMergeStones,
  consumeHandCard,
  createCardStock,
  recordNormalContact,
  type CardStockState,
} from './cards/cardStock';
import { DangerTracker } from './danger';
import { DropBag, type ResonancePhase } from './dropBag';
import { resolveMerges } from './mergeResolver';
import { SeededRandom } from './random';
import { mergeScore, resonanceBonus } from './scoring';
import type {
  AttractionEffect,
  CardAvailability,
  CardEffectsState,
  CardUsePreparation,
  CardUseRequest,
  EntityOrigin,
  LogicalEntity,
  PhysicsStepObservation,
  PresentationKind,
  PresentationState,
  SceneCommand,
  SessionEvent,
  SessionResult,
  SessionState,
  SessionStatus,
  TimedTargetEffect,
  WindDirection,
} from './types';

const CARD_EFFECT_DURATION_MS = 5_000;
const OBSERVE_DURATION_MS = 30_000;

type MutableEntity = {
  entityId: string;
  characterId: CharacterId;
  lifecycle: 'active' | 'staged' | 'consumed';
  origin: EntityOrigin;
  normalDropSequence: number | null;
  bornStep: number;
  mergeEligibleStep: number;
  dangerGraceUntilMs: number;
  placed: boolean;
  dropConfirmed: boolean;
};

type PendingSpecial = Readonly<{
  entity: MutableEntity;
  kind: Exclude<CharacterSpecialKind, null>;
}>;

type MergeApplication = Readonly<{
  phaseTriggerEntityId: string | null;
  pendingSpecial: PendingSpecial | null;
  extraSpecialEntities: readonly MutableEntity[];
  stateChanged: boolean;
}>;

export type GameSessionOptions = Readonly<{
  mode?: GameModeId;
  seed: number;
  observerReached?: boolean;
  createRunId?: () => string;
}>;

export type ScenarioEntity = Readonly<{
  characterId: CharacterId;
  x: number;
  y: number;
  placed?: boolean;
}>;

export class GameSession {
  private mode: GameModeId;
  private readonly createRunId: () => string;
  private runId: string;
  private clearedThisRun = false;
  private clearTimeMs: number | null = null;
  private generation = 1;
  private seed: number;
  private status: SessionStatus = 'ready';
  private phase: ResonancePhase = 1;
  private score = 0;
  private chain = 0;
  private maxChain = 0;
  private highestStage = 1;
  private dropSequence = 0;
  private entitySequence = 0;
  private presentationSequence = 0;
  private activeDropEntityId: string | null = null;
  private pendingDropFinish: Readonly<{ entityId: string; dueAtMs: number }> | null = null;
  private lastPlacedEntityId: string | null = null;
  private activePresentation: PresentationState | null = null;
  private gameOverEntityId: string | null = null;
  private gameplayTimeMs = 0;
  private observerReached: boolean;
  private cardRandom: SeededRandom;
  private effectRandom: SeededRandom;
  private bag: DropBag;
  private currentCharacterId: CharacterId;
  private nextCharacterId: CharacterId;
  private readonly entities = new Map<string, MutableEntity>();
  private readonly danger = new DangerTracker();
  private card: CardStockState;
  private effects: CardEffectsState = emptyEffects();
  private cachedState: SessionState | null = null;

  public constructor(options: GameSessionOptions) {
    this.mode = options.mode ?? DEFAULT_GAME_MODE;
    this.createRunId = options.createRunId ?? (() => crypto.randomUUID());
    this.runId = this.createRunId();
    this.seed = options.seed >>> 0;
    this.observerReached = options.observerReached ?? false;
    const drawState = createDrawState(this.seed, this.mode);
    this.cardRandom = drawState.cardRandom;
    this.effectRandom = drawState.effectRandom;
    this.bag = drawState.bag;
    this.currentCharacterId = drawState.currentCharacterId;
    this.nextCharacterId = drawState.nextCharacterId;
    this.card = drawState.card;
  }

  public getState(): SessionState {
    if (this.cachedState) return this.cachedState;
    const entities = Object.freeze([...this.entities.values()].map((entity) => Object.freeze({ ...entity })));
    const activeEntity = this.activeDropEntityId ? this.entities.get(this.activeDropEntityId) : undefined;
    const secondNextCharacterId = this.activeDropEntityId !== null
      ? this.nextCharacterId
      : this.bag.peek(1)[0] ?? this.nextCharacterId;
    const canControl = Boolean(
      this.status === 'playing' &&
      activeEntity &&
      activeEntity.lifecycle === 'active' &&
      !activeEntity.placed &&
      !activeEntity.dropConfirmed,
    );
    const dropView = Object.freeze({
      activeEntityId: this.activeDropEntityId,
      currentCharacterId: this.currentCharacterId,
      nextCharacterId: this.nextCharacterId,
      secondNextCharacterId,
      lastPlacedEntityId: this.lastPlacedEntityId,
      canControl,
      slotActive: this.activeDropEntityId !== null,
      finishDueAtMs: this.pendingDropFinish?.dueAtMs ?? null,
    });
    const card = Object.freeze({
      ...this.card,
      hand: Object.freeze(this.card.hand.map((entry) => Object.freeze({ ...entry }))),
    });
    const cardEffects = freezeEffects(this.effects);
    this.cachedState = Object.freeze({
      mode: this.mode,
      runId: this.runId,
      clearedThisRun: this.clearedThisRun,
      clearTimeMs: this.clearTimeMs,
      generation: this.generation,
      status: this.status,
      seed: this.seed,
      gameplayTimeMs: this.gameplayTimeMs,
      phase: this.phase,
      score: this.score,
      chain: this.chain,
      maxChain: this.maxChain,
      highestStage: this.highestStage,
      currentCharacterId: this.currentCharacterId,
      nextCharacterId: this.nextCharacterId,
      dropSequence: this.dropSequence,
      activeDropEntityId: this.activeDropEntityId,
      dropView,
      entities,
      activePresentation: this.activePresentation ? Object.freeze({ ...this.activePresentation }) : null,
      gameOverEntityId: this.gameOverEntityId,
      observerReached: this.observerReached,
      card,
      cardAvailability: Object.freeze(this.card.hand.map((entry) => Object.freeze(this.availability(entry.instanceId)))),
      cardEffects,
    });
    return this.cachedState;
  }

  public start(): SessionResult {
    if (this.status !== 'ready') return this.result();
    this.status = 'playing';
    return this.result([], [], true);
  }

  public beginDrop(
    x: number,
    y: number,
    step: number,
    nowMs: number,
    initialSpeed = DROP_INITIAL_SPEED,
  ): SessionResult {
    if (this.status !== 'playing' || this.activeDropEntityId !== null) return this.result();
    this.chain = 0;
    this.dropSequence += 1;
    const entity = this.createEntity(this.currentCharacterId, 'active', 'normal', this.dropSequence, step, nowMs, false, step);
    this.activeDropEntityId = entity.entityId;
    this.currentCharacterId = this.nextCharacterId;
    this.nextCharacterId = this.bag.draw();
    return this.result(
      [{ type: 'create-active-body', entityId: entity.entityId, characterId: entity.characterId, x, y, initialVelocity: { x: 0, y: initialSpeed } }],
      [{ type: 'drop-created', entityId: entity.entityId, characterId: entity.characterId, origin: 'normal' }],
      true,
    );
  }

  public confirmDrop(entityId: string, nowMs: number): SessionResult {
    const entity = this.entities.get(entityId);
    if (!entity || entityId !== this.activeDropEntityId || entity.placed || entity.dropConfirmed || this.status !== 'playing') {
      return this.result();
    }
    entity.dropConfirmed = true;
    this.scheduleDropFinish(entityId, nowMs, DROP_INTERVAL_MS);
    return this.result([], [], true);
  }

  /** Kept as a narrow compatibility boundary for integrations that finish a normal slot directly. */
  public finishDropWindow(entityId: string): SessionResult {
    if (this.activeDropEntityId !== entityId) return this.result();
    this.activeDropEntityId = null;
    this.pendingDropFinish = null;
    return this.result([], [{ type: 'drop-finished', entityId }], true);
  }

  public useCard(request: CardUseRequest, preparation: CardUsePreparation, nowMs: number): SessionResult {
    const held = this.card.hand.find((entry) => entry.instanceId === request.instanceId);
    if (!held) return this.result();
    const card = getCard(held.cardId);
    const failed = (reason: string): SessionResult => this.result([], [{ type: 'card-use', cardId: card.id, success: false, reason }]);
    if (request.generation !== this.generation) return failed('操作の対象が変わりました');
    const availability = this.availability(request.instanceId);
    if (!availability.enabled) return failed(availability.reason ?? '使用できません');

    const expectedTarget = this.expectedTarget(card.id);
    if (expectedTarget !== null && request.targetEntityId !== expectedTarget) return failed('操作の対象が変わりました');
    const commands: SceneCommand[] = [];

    switch (card.id) {
      case 'destroy':
        this.removeEntity(expectedTarget!, commands);
        break;
      case 'swap': {
        const entity = this.entities.get(expectedTarget!);
        if (!entity || !preparation.replacementCandidates?.includes(this.currentCharacterId)) return failed('配置スペースがありません');
        const original = entity.characterId;
        entity.characterId = this.currentCharacterId;
        this.currentCharacterId = original;
        commands.push({
          type: 'replace-body',
          entityId: entity.entityId,
          characterId: entity.characterId,
          massMultiplier: this.effects.gravityEntityIds.includes(entity.entityId) ? 2 : 1,
        });
        break;
      }
      case 'observe':
        this.effects = { ...this.effects, observeUntilMs: nowMs + OBSERVE_DURATION_MS };
        break;
      case 'execute':
        if (this.activeDropEntityId) {
          this.currentCharacterId = this.nextCharacterId;
          this.nextCharacterId = this.bag.draw();
        } else {
          this.nextCharacterId = this.bag.draw();
        }
        break;
      case 'reincarnate': {
        const entity = this.entities.get(expectedTarget!);
        const candidates = (preparation.replacementCandidates ?? []).filter((candidate) => candidate !== entity?.characterId);
        if (!entity || candidates.length === 0) return failed('配置スペースがありません');
        entity.characterId = this.pickWeighted(candidates);
        commands.push({
          type: 'replace-body',
          entityId: entity.entityId,
          characterId: entity.characterId,
          massMultiplier: this.effects.gravityEntityIds.includes(entity.entityId) ? 2 : 1,
        });
        break;
      }
      case 'gravity':
        this.effects = { ...this.effects, gravityEntityIds: [...this.effects.gravityEntityIds, expectedTarget!] };
        commands.push({ type: 'set-mass-multiplier', entityId: expectedTarget!, multiplier: 2 });
        break;
      case 'buoyancy':
        this.danger.remove(expectedTarget!);
        this.effects = { ...this.effects, buoyancy: { targetEntityId: expectedTarget!, untilMs: nowMs + CARD_EFFECT_DURATION_MS } };
        break;
      case 'attraction':
        this.danger.remove(this.lastPlacedEntityId!);
        this.effects = {
          ...this.effects,
          attraction: { sourceEntityId: this.lastPlacedEntityId!, targetEntityId: expectedTarget!, untilMs: nowMs + CARD_EFFECT_DURATION_MS },
        };
        break;
      case 'reinforcement': {
        const choices = Object.entries(preparation.reinforcementPositions ?? {})
          .filter(([, positions]) => positions.length > 0) as [CharacterId, readonly number[]][];
        if (choices.length === 0) return failed('出現する場所がありません');
        const characters = choices.map(([characterId]) => characterId);
        const characterId = this.pickWeighted(characters);
        const positions = preparation.reinforcementPositions?.[characterId] ?? [];
        const x = positions[this.effectRandom.nextInt(positions.length)];
        if (x === undefined) return failed('出現する場所がありません');
        const entity = this.createEntity(characterId, 'active', 'reinforcement', null, 0, nowMs, false, 0);
        commands.push({
          type: 'create-active-body',
          entityId: entity.entityId,
          characterId,
          x,
          y: preparation.reinforcementY?.[characterId] ?? 60,
          initialVelocity: { x: 0, y: DROP_INITIAL_SPEED },
        });
        break;
      }
      case 'wind': {
        const direction: WindDirection = this.effectRandom.nextInt(2) === 0 ? 'left' : 'right';
        this.effects = { ...this.effects, wind: { entityId: expectedTarget!, direction } };
        break;
      }
    }

    this.card = consumeHandCard(this.card, request.instanceId, card.cost, () => getCardAtIndex(this.cardRandom.nextInt(10)));
    const events: SessionEvent[] = [{ type: 'card-use', cardId: card.id, success: true }];
    if (card.id === 'reinforcement') {
      const command = commands.find((candidate) => candidate.type === 'create-active-body');
      if (command?.type === 'create-active-body') {
        events.push({ type: 'drop-created', entityId: command.entityId, characterId: command.characterId, origin: 'reinforcement' });
      }
    }
    return this.result(commands, events, true);
  }

  public pause(): SessionResult {
    if (this.status !== 'playing') return this.result();
    this.status = 'paused';
    return this.result([], [], true);
  }

  public resume(): SessionResult {
    if (this.status !== 'paused') return this.result();
    this.status = 'playing';
    return this.result([], [], true);
  }

  public applyPhysicsStep(observation: PhysicsStepObservation): SessionResult {
    if (this.status !== 'playing') return this.result();
    const escaped = observation.escapedEntityIds?.find((id) => this.entities.get(id)?.lifecycle === 'active');
    if (escaped) return this.forceGameOver(escaped);
    const previousGameplaySecond = Math.floor(this.gameplayTimeMs / 1_000);
    this.gameplayTimeMs = observation.nowMs;
    const commands: SceneCommand[] = [];
    const events: SessionEvent[] = [];
    let stateChanged = previousGameplaySecond !== Math.floor(this.gameplayTimeMs / 1_000) || this.expireEffects(observation.nowMs, commands);
    if (this.pendingDropFinish && observation.nowMs >= this.pendingDropFinish.dueAtMs) {
      const entityId = this.pendingDropFinish.entityId;
      if (this.activeDropEntityId === entityId) {
        this.activeDropEntityId = null;
        events.push({ type: 'drop-finished', entityId });
        stateChanged = true;
      }
      this.pendingDropFinish = null;
    }
    stateChanged = this.markFirstContacts(observation.firstContacts, observation.nowMs) || stateChanged;
    const merges = this.applyMerges(observation, commands, events);
    stateChanged = merges.stateChanged || stateChanged;
    const phaseChanged = this.advancePhase(merges.phaseTriggerEntityId, commands, events);
    stateChanged = phaseChanged || stateChanged;
    stateChanged = this.fireWind(observation.firstContacts, commands) || stateChanged;
    stateChanged = this.expireEffects(observation.nowMs, commands) || stateChanged;
    stateChanged = this.resolveStepOutcome(phaseChanged, merges.pendingSpecial, [...merges.extraSpecialEntities], commands, events) || stateChanged;
    return this.result(commands, events, stateChanged);
  }

  public settlePresentation(presentationId: number, outcome: 'success' | 'failure' | 'cancel'): SessionResult {
    const presentation = this.activePresentation;
    if (!presentation || presentation.id !== presentationId || this.status !== 'presenting') return this.result();
    const commands: SceneCommand[] = [];
    if (presentation.kind !== 'phase') {
      const entity = this.entities.get(presentation.entityId);
      if (entity?.lifecycle === 'staged') {
        entity.lifecycle = 'active';
        commands.push({ type: 'activate-body', entityId: entity.entityId });
      }
    }
    void outcome;
    this.activePresentation = null;
    this.status = 'playing';
    return this.result(commands, [], true);
  }

  public forceGameOver(entityId: string): SessionResult {
    if (this.status === 'game-over') return this.result();
    const commands = this.activateAllStaged();
    this.activePresentation = null;
    this.status = 'game-over';
    this.gameOverEntityId = entityId;
    this.pendingDropFinish = null;
    this.clearAllEffects();
    return this.result(commands, [{ type: 'game-over', entityId }], true);
  }

  public restart(seed = this.seed, mode = this.mode, observerReached = this.observerReached): SessionResult {
    this.mode = mode;
    this.observerReached = observerReached;
    this.runId = this.createRunId();
    this.clearedThisRun = false;
    this.clearTimeMs = null;
    const commands: SceneCommand[] = [...this.entities.keys()].map((entityId) => ({ type: 'remove-body', entityId }));
    this.generation += 1;
    this.seed = seed >>> 0;
    this.status = 'ready';
    this.phase = 1;
    this.score = 0;
    this.chain = 0;
    this.maxChain = 0;
    this.highestStage = 1;
    this.dropSequence = 0;
    this.entitySequence = 0;
    this.presentationSequence = 0;
    this.activeDropEntityId = null;
    this.pendingDropFinish = null;
    this.lastPlacedEntityId = null;
    this.activePresentation = null;
    this.gameOverEntityId = null;
    this.gameplayTimeMs = 0;
    this.entities.clear();
    this.danger.clear();
    this.effects = emptyEffects();
    const drawState = createDrawState(this.seed, this.mode);
    this.cardRandom = drawState.cardRandom;
    this.effectRandom = drawState.effectRandom;
    this.bag = drawState.bag;
    this.currentCharacterId = drawState.currentCharacterId;
    this.nextCharacterId = drawState.nextCharacterId;
    this.card = drawState.card;
    return this.result(commands, [], true);
  }

  public loadScenario(scenario: readonly ScenarioEntity[], step: number, nowMs: number): SessionResult {
    if (this.status !== 'playing') return this.result();
    const commands: SceneCommand[] = [];
    for (const item of scenario) {
      const entity = this.createEntity(item.characterId, 'active', 'scenario', null, step, nowMs, item.placed ?? true, step);
      this.highestStage = Math.max(this.highestStage, getCharacter(item.characterId).stage);
      commands.push({ type: 'create-active-body', entityId: entity.entityId, characterId: entity.characterId, x: item.x, y: item.y });
    }
    return this.result(commands, [], scenario.length > 0);
  }

  /** Development-only scenario setup; card activation still goes through useCard. */
  public configureCardScenario(cardIds: readonly CardId[], stones = 99): SessionResult {
    if (this.status !== 'playing') return this.result();
    const hand = cardIds.slice(0, CARD_HAND_LIMIT).map((cardId, index) => ({ instanceId: `debug-card-${index + 1}`, cardId, slot: index }));
    this.card = {
      stones: Math.max(0, Math.min(99, Math.floor(stones))),
      hand,
      normalContactsTowardReplacement: 0,
      nextInstanceSequence: hand.length + 1,
    };
    return this.result([], [], true);
  }

  public dangerWarningElapsed(entityId: string, nowMs: number): number {
    return this.danger.warningElapsed(entityId, nowMs);
  }

  private availability(instanceId: string): CardAvailability {
    const held = this.card.hand.find((entry) => entry.instanceId === instanceId);
    if (!held) return { instanceId, enabled: false, reason: 'カードがありません' };
    const card = getCard(held.cardId);
    if (this.status !== 'playing') return { instanceId, enabled: false, reason: '今は使用できません' };
    if (this.card.stones < card.cost) return { instanceId, enabled: false, reason: '布石が足りません' };
    const target = this.expectedTarget(card.id);
    if (needsTarget(card.id) && target === null) return { instanceId, enabled: false, reason: '対象のキャラがいません' };
    if (card.id === 'attraction' && this.lastPlacedEntityId === null) return { instanceId, enabled: false, reason: '対象のキャラがいません' };
    if (card.id === 'swap' && target && this.entities.get(target)?.characterId === this.currentCharacterId) {
      return { instanceId, enabled: false, reason: '同じキャラです' };
    }
    if (card.id === 'observe' && this.effects.observeUntilMs !== null) return { instanceId, enabled: false, reason: 'すでに効果中です' };
    if ((card.id === 'buoyancy' || card.id === 'attraction') && (this.effects.buoyancy || this.effects.attraction)) {
      return { instanceId, enabled: false, reason: 'すでに効果中です' };
    }
    if (card.id === 'gravity' && target && this.effects.gravityEntityIds.includes(target)) return { instanceId, enabled: false, reason: 'すでに効果中です' };
    if (card.id === 'wind' && this.effects.wind?.entityId === target) return { instanceId, enabled: false, reason: 'すでに効果中です' };
    return { instanceId, enabled: true, reason: null };
  }

  private expectedTarget(cardId: CardId): string | null {
    if (cardId === 'destroy' || cardId === 'buoyancy') return this.lastPlacedEntityId;
    if (cardId === 'swap' || cardId === 'reincarnate' || cardId === 'gravity' || cardId === 'attraction' || cardId === 'wind') {
      const entity = this.activeDropEntityId ? this.entities.get(this.activeDropEntityId) : undefined;
      return entity && entity.lifecycle === 'active' && !entity.placed && !entity.dropConfirmed ? entity.entityId : null;
    }
    return null;
  }

  private markFirstContacts(firstContacts: readonly string[], nowMs: number): boolean {
    let stateChanged = false;
    for (const entityId of [...new Set(firstContacts)].sort()) {
      const entity = this.entities.get(entityId);
      if (!entity || entity.lifecycle !== 'active' || entity.placed) continue;
      entity.placed = true;
      if (entity.origin === 'normal') {
        this.lastPlacedEntityId = entity.entityId;
        this.card = recordNormalContact(this.card, () => {
          const index = this.cardRandom.nextInt(10);
          return getCardAtIndex(index);
        });
        if (this.activeDropEntityId === entity.entityId) this.scheduleDropFinish(entity.entityId, nowMs);
      }
      stateChanged = true;
    }
    return stateChanged;
  }

  private applyMerges(observation: PhysicsStepObservation, commands: SceneCommand[], events: SessionEvent[]): MergeApplication {
    const accepted = resolveMerges(this.entities, observation.contacts, observation.step);
    let phaseTriggerEntityId: string | null = null;
    let pendingSpecial: PendingSpecial | null = null;
    const extraSpecialEntities: MutableEntity[] = [];
    let stateChanged = false;
    for (const merge of accepted) {
      const left = this.entities.get(merge.a);
      const right = this.entities.get(merge.b);
      if (!left || !right) continue;
      if (left.entityId === this.activeDropEntityId || right.entityId === this.activeDropEntityId) this.scheduleDropFinish(this.activeDropEntityId!, observation.nowMs);
      this.removeEntity(left.entityId, commands);
      this.removeEntity(right.entityId, commands);
      const generated = getCharacter(merge.generatedCharacterId);
      const specialKind = generated.specialKind;
      const entity = this.createEntity(generated.id, specialKind === null ? 'active' : 'staged', 'merge', null, observation.step, observation.nowMs + MERGE_DANGER_GRACE_MS, true, observation.step + 1);
      commands.push({
        type: specialKind === null ? 'create-active-body' : 'create-staged-body',
        entityId: entity.entityId,
        characterId: entity.characterId,
        x: merge.point.x,
        y: merge.point.y,
      });
      this.chain += 1;
      this.maxChain = Math.max(this.maxChain, this.chain);
      this.highestStage = Math.max(this.highestStage, generated.stage);
      this.card = awardMergeStones(this.card);
      const awardedScore = mergeScore(generated.id, this.chain);
      this.score += awardedScore;
      events.push({ type: 'merge', entityId: entity.entityId, characterId: entity.characterId, consumedEntityIds: [left.entityId, right.entityId], chain: this.chain, awardedScore });
      events.push({ type: 'score', score: this.score, delta: awardedScore, source: 'merge' });
      const triggerStage = this.phase < getGameMode(this.mode).phases.length ? getModePhase(this.mode, this.phase).goalStage : null;
      if (triggerStage === generated.stage) phaseTriggerEntityId = entity.entityId;
      if (specialKind !== null) {
        if (specialKind === 'observer' && !this.clearedThisRun) {
          this.clearedThisRun = true;
          this.clearTimeMs = Math.max(1, Math.round(this.gameplayTimeMs));
          events.push({ type: 'run-cleared', runId: this.runId, generation: this.generation });
        }
        if (pendingSpecial === null) pendingSpecial = { entity, kind: specialKind };
        else extraSpecialEntities.push(entity);
        if (specialKind === 'observer' && !this.observerReached) {
          this.observerReached = true;
          events.push({ type: 'achievement', text: '実績解除：盤理の観測者へ到達' });
        }
      }
      stateChanged = true;
    }
    return { phaseTriggerEntityId, pendingSpecial, extraSpecialEntities, stateChanged };
  }

  private advancePhase(phaseTriggerEntityId: string | null, commands: SceneCommand[], events: SessionEvent[]): boolean {
    if (phaseTriggerEntityId === null || this.phase >= getGameMode(this.mode).phases.length) return false;
    const trigger = this.entities.get(phaseTriggerEntityId);
    if (!trigger) return false;
    const removed = [...this.entities.values()].filter((entity) => entity.entityId !== phaseTriggerEntityId && entity.lifecycle !== 'consumed');
    const bonus = resonanceBonus(removed.map((entity) => entity.characterId));
    for (const entity of removed) this.removeEntity(entity.entityId, commands);
    const carry = getGameMode(this.mode).carryMilestone;
    if (!carry) this.removeEntity(trigger.entityId, commands);
    this.score += bonus;
    if (bonus > 0) events.push({ type: 'score', score: this.score, delta: bonus, source: 'resonance' });
    this.phase = (this.phase + 1) as ResonancePhase;
    this.bag.switchPhase(this.phase);
    this.currentCharacterId = this.bag.draw();
    this.nextCharacterId = this.bag.draw();
    this.activeDropEntityId = null;
    this.pendingDropFinish = null;
    this.lastPlacedEntityId = null;
    this.danger.clear();
    this.effects = { ...emptyEffects(), observeUntilMs: this.effects.observeUntilMs };
    const presentation = { ...this.createPresentation('phase', trigger.entityId, this.phase), ...(!carry ? { milestoneCharacterId: trigger.characterId } : {}) };
    this.activePresentation = presentation;
    this.status = 'presenting';
    if (carry) commands.push({ type: 'reposition-body', entityId: trigger.entityId, x: FIELD_WIDTH / 2, y: FIELD_HEIGHT - Math.max(82, getCharacterForPhase(trigger.characterId, this.phase, this.mode).baseSize / 2 + 36) });
    events.push({ type: 'phase-changed', phase: this.phase, bonus });
    events.push({ type: 'presentation-requested', presentation });
    return true;
  }

  private resolveStepOutcome(
    phaseChanged: boolean,
    pendingSpecial: PendingSpecial | null,
    extraSpecialEntities: MutableEntity[],
    commands: SceneCommand[],
    events: SessionEvent[],
  ): boolean {
    if (phaseChanged || pendingSpecial === null) return false;
    const presentation = this.createPresentation(pendingSpecial.kind, pendingSpecial.entity.entityId);
    this.activePresentation = presentation;
    this.status = 'presenting';
    events.push({ type: 'presentation-requested', presentation });
    for (const entity of extraSpecialEntities) {
      entity.lifecycle = 'active';
      commands.push({ type: 'activate-body', entityId: entity.entityId });
    }
    return true;
  }

  private fireWind(firstContacts: readonly string[], commands: SceneCommand[]): boolean {
    const wind = this.effects.wind;
    if (!wind || !firstContacts.includes(wind.entityId)) return false;
    this.effects = { ...this.effects, wind: null };
    if (this.entities.has(wind.entityId)) commands.push({ type: 'apply-wind', entityId: wind.entityId, direction: wind.direction });
    return true;
  }

  private expireEffects(nowMs: number, commands: SceneCommand[]): boolean {
    let changed = false;
    let effects = this.effects;
    const expireTarget = (effect: TimedTargetEffect | null): TimedTargetEffect | null => {
      if (!effect) return null;
      if (!this.entities.has(effect.targetEntityId)) {
        changed = true;
        return null;
      }
      if (nowMs < effect.untilMs) return effect;
      const entity = this.entities.get(effect.targetEntityId);
      if (entity) entity.dangerGraceUntilMs = nowMs + MERGE_DANGER_GRACE_MS;
      commands.push({ type: 'zero-upward-velocity', entityId: effect.targetEntityId } as SceneCommand);
      changed = true;
      return null;
    };
    const buoyancy = expireTarget(effects.buoyancy);
    let attraction = effects.attraction;
    if (attraction && (!this.entities.has(attraction.sourceEntityId) || !this.entities.has(attraction.targetEntityId) || nowMs >= attraction.untilMs)) {
      const source = this.entities.get(attraction.sourceEntityId);
      if (source) source.dangerGraceUntilMs = nowMs + MERGE_DANGER_GRACE_MS;
      attraction = null;
      changed = true;
    }
    let observeUntilMs = effects.observeUntilMs;
    if (observeUntilMs !== null && nowMs >= observeUntilMs) {
      observeUntilMs = null;
      changed = true;
    }
    const gravityEntityIds = effects.gravityEntityIds.filter((id) => this.entities.has(id));
    const wind = effects.wind && this.entities.has(effects.wind.entityId) ? effects.wind : null;
    if (gravityEntityIds.length !== effects.gravityEntityIds.length || wind !== effects.wind) changed = true;
    effects = { observeUntilMs, buoyancy, attraction, gravityEntityIds, wind };
    if (changed) this.effects = effects;
    return changed;
  }

  private removeEntity(entityId: string, commands: SceneCommand[]): void {
    const entity = this.entities.get(entityId);
    if (!entity) return;
    entity.lifecycle = 'consumed';
    this.entities.delete(entityId);
    this.danger.remove(entityId);
    if (this.lastPlacedEntityId === entityId) this.lastPlacedEntityId = null;
    const buoyancy = this.effects.buoyancy?.targetEntityId === entityId ? null : this.effects.buoyancy;
    const attraction = this.effects.attraction && (this.effects.attraction.sourceEntityId === entityId || this.effects.attraction.targetEntityId === entityId)
      ? null
      : this.effects.attraction;
    this.effects = {
      ...this.effects,
      buoyancy,
      attraction,
      gravityEntityIds: this.effects.gravityEntityIds.filter((id) => id !== entityId),
      wind: this.effects.wind?.entityId === entityId ? null : this.effects.wind,
    };
    commands.push({ type: 'remove-body', entityId });
  }

  private scheduleDropFinish(entityId: string, nowMs: number, delayMs = DROP_SETTLE_MS): void {
    if (this.activeDropEntityId !== entityId || this.pendingDropFinish) return;
    this.pendingDropFinish = { entityId, dueAtMs: nowMs + delayMs };
  }

  private isDangerExcluded(entityId: string): boolean {
    return this.effects.buoyancy?.targetEntityId === entityId || this.effects.attraction?.sourceEntityId === entityId;
  }

  private clearAllEffects(): void {
    this.effects = emptyEffects();
  }

  private pickWeighted(candidates: readonly CharacterId[]): CharacterId {
    const allowed = new Set(candidates);
    const all: CharacterId[] = [];
    this.bag.candidateIds().forEach((id, offset) => {
      if (allowed.has(id)) for (let count = 0; count < getGameMode(this.mode).dropCounts[offset]!; count += 1) all.push(id);
    });
    if (all.length === 0) throw new Error('No eligible card-effect candidate');
    return all[this.effectRandom.nextInt(all.length)]!;
  }

  private createEntity(
    characterId: CharacterId,
    lifecycle: MutableEntity['lifecycle'],
    origin: EntityOrigin,
    normalDropSequence: number | null,
    bornStep: number,
    dangerGraceUntilMs: number,
    placed: boolean,
    mergeEligibleStep: number,
  ): MutableEntity {
    this.entitySequence += 1;
    const entity: MutableEntity = {
      entityId: `g${this.generation}-e${this.entitySequence}`,
      characterId,
      lifecycle,
      origin,
      normalDropSequence,
      bornStep,
      mergeEligibleStep,
      dangerGraceUntilMs,
      placed,
      dropConfirmed: false,
    };
    this.entities.set(entity.entityId, entity);
    return entity;
  }

  private createPresentation(kind: PresentationKind, entityId: string, phase?: ResonancePhase): PresentationState {
    this.presentationSequence += 1;
    return phase === undefined
      ? { id: this.presentationSequence, generation: this.generation, kind, entityId }
      : { id: this.presentationSequence, generation: this.generation, kind, entityId, phase, mode: this.mode };
  }

  private activateAllStaged(): SceneCommand[] {
    const commands: SceneCommand[] = [];
    for (const entity of this.entities.values()) {
      if (entity.lifecycle === 'staged') {
        entity.lifecycle = 'active';
        commands.push({ type: 'activate-body', entityId: entity.entityId });
      }
    }
    return commands;
  }

  private result(commands: readonly SceneCommand[] = [], events: readonly SessionEvent[] = [], stateChanged = false): SessionResult {
    if (stateChanged) this.cachedState = null;
    return { state: this.getState(), commands, events };
  }
}

function needsTarget(cardId: CardId): boolean {
  return cardId !== 'observe' && cardId !== 'execute' && cardId !== 'reinforcement';
}

function emptyEffects(): CardEffectsState {
  return { observeUntilMs: null, buoyancy: null, attraction: null, gravityEntityIds: [], wind: null };
}

function freezeEffects(effects: CardEffectsState): CardEffectsState {
  return Object.freeze({
    ...effects,
    gravityEntityIds: Object.freeze([...effects.gravityEntityIds]),
    buoyancy: effects.buoyancy ? Object.freeze({ ...effects.buoyancy }) : null,
    attraction: effects.attraction ? Object.freeze({ ...effects.attraction }) : null,
    wind: effects.wind ? Object.freeze({ ...effects.wind }) : null,
  });
}

function getCardAtIndex(index: number): CardId {
  const ids: readonly CardId[] = ['destroy', 'swap', 'observe', 'execute', 'reincarnate', 'gravity', 'buoyancy', 'attraction', 'reinforcement', 'wind'];
  const id = ids[index];
  if (!id) throw new Error('Invalid card draw index');
  return id;
}

function createDrawState(seed: number, mode: GameModeId): Readonly<{
  cardRandom: SeededRandom;
  effectRandom: SeededRandom;
  bag: DropBag;
  currentCharacterId: CharacterId;
  nextCharacterId: CharacterId;
  card: CardStockState;
}> {
  const random = new SeededRandom(seed);
  const bag = new DropBag(1, random, mode);
  return {
    cardRandom: new SeededRandom(seed ^ 0x9e37_79b9),
    effectRandom: new SeededRandom(seed ^ 0x85eb_ca6b),
    bag,
    currentCharacterId: bag.draw(),
    nextCharacterId: bag.draw(),
    card: createCardStock(),
  };
}

export type { LogicalEntity };
